/**
 * Left-panel layout (Customize sidebar).
 *
 * Mirrors the server contract in
 * zephix-backend/src/modules/users/dto/sidebar-layout.dto.ts. The server
 * validates and stores it; this module owns the pure rules the dialog and the
 * sidebar share. A layout is a DISPLAY preference: it can hide or reorder rows,
 * never reveal one. Role, capability and feature-switch checks still decide
 * whether a listed row renders.
 *
 * `home` is not an item: Home is always the first row and cannot be changed.
 */

export const SIDEBAR_ITEM_IDS = [
  "inbox",
  "mywork",
  "favorites",
  "workspaces",
  "dashboards",
  "shared",
  "portfolios_programs",
] as const;

/**
 * Ids an earlier build could save. They no longer exist: Templates is reached
 * from the Template Center and Resources has no page yet. A saved layout that
 * still lists them is read with those ids dropped, so it is not discarded.
 */
const RETIRED_IDS: readonly string[] = ["templates", "resources"];

export type SidebarItemId = (typeof SIDEBAR_ITEM_IDS)[number];

export type SidebarDensity = "comfortable" | "compact";

export const SIDEBAR_LAYOUT_VERSION = 1 as const;

export type SidebarLayout = {
  v: typeof SIDEBAR_LAYOUT_VERSION;
  /** Rows shown, top to bottom. Ids not listed are "available to add". */
  order: SidebarItemId[];
  density: SidebarDensity;
};

/**
 * link    — a single navigation row
 * section — a labelled section with its own content (gets a divider above)
 * inline  — rendered inside another item; has no position of its own
 */
export type SidebarItemKind = "link" | "section" | "inline";

type ItemMeta = {
  label: string;
  /** One sentence, 12 words or fewer (design standard §3). */
  description: string;
  kind: SidebarItemKind;
  /** Always shown; cannot be removed. */
  locked?: boolean;
};

export const SIDEBAR_ITEM_META: Record<SidebarItemId, ItemMeta> = {
  inbox: {
    label: "Inbox",
    description: "Notifications and requests for you.",
    kind: "link",
  },
  mywork: {
    label: "My Work",
    description: "Tasks assigned to you.",
    kind: "link",
  },
  favorites: {
    label: "Favorites",
    description: "Workspaces, projects and dashboards you starred.",
    kind: "section",
  },
  workspaces: {
    label: "Workspaces",
    description: "Every workspace and its projects.",
    kind: "section",
    locked: true,
  },
  dashboards: {
    label: "Dashboards",
    description: "Published dashboards.",
    kind: "link",
  },
  shared: {
    label: "Shared",
    description: "Dashboards shared with you, when any exist.",
    kind: "section",
  },
  portfolios_programs: {
    label: "Portfolios & Programs",
    description: "Shown inside an open workspace.",
    kind: "inline",
  },
};

export const DEFAULT_SIDEBAR_LAYOUT: SidebarLayout = {
  v: SIDEBAR_LAYOUT_VERSION,
  order: [
    "inbox",
    "mywork",
    "favorites",
    "workspaces",
    "portfolios_programs",
    "dashboards",
    "shared",
  ],
  density: "comfortable",
};

const KNOWN: readonly string[] = SIDEBAR_ITEM_IDS;

export function isLocked(id: SidebarItemId): boolean {
  return SIDEBAR_ITEM_META[id].locked === true;
}

export function isInline(id: SidebarItemId): boolean {
  return SIDEBAR_ITEM_META[id].kind === "inline";
}

/**
 * Accepts anything the API might return. An invalid or missing value yields the
 * default layout, so a bad stored value can never blank the sidebar.
 */
export function parseSidebarLayout(raw: unknown): SidebarLayout {
  if (raw === null || typeof raw !== "object") return DEFAULT_SIDEBAR_LAYOUT;
  const c = raw as Record<string, unknown>;
  if (c.v !== SIDEBAR_LAYOUT_VERSION || !Array.isArray(c.order)) {
    return DEFAULT_SIDEBAR_LAYOUT;
  }
  if (c.density !== "comfortable" && c.density !== "compact") {
    return DEFAULT_SIDEBAR_LAYOUT;
  }
  const seen = new Set<string>();
  const order: SidebarItemId[] = [];
  for (const id of c.order) {
    if (typeof id === "string" && RETIRED_IDS.includes(id)) continue;
    if (typeof id !== "string" || !KNOWN.includes(id) || seen.has(id)) {
      return DEFAULT_SIDEBAR_LAYOUT;
    }
    seen.add(id);
    order.push(id as SidebarItemId);
  }
  if (!seen.has("workspaces")) return DEFAULT_SIDEBAR_LAYOUT;
  return { v: SIDEBAR_LAYOUT_VERSION, order, density: c.density };
}

/** Ids not in the layout, in canonical order — the rows the user has hidden. */
export function hiddenItems(layout: SidebarLayout): SidebarItemId[] {
  return SIDEBAR_ITEM_IDS.filter((id) => !layout.order.includes(id));
}

export function addItem(layout: SidebarLayout, id: SidebarItemId): SidebarLayout {
  if (layout.order.includes(id)) return layout;
  return { ...layout, order: [...layout.order, id] };
}

export function removeItem(
  layout: SidebarLayout,
  id: SidebarItemId,
): SidebarLayout {
  if (isLocked(id) || !layout.order.includes(id)) return layout;
  return { ...layout, order: layout.order.filter((x) => x !== id) };
}

/**
 * Moves a positioned row one step up (-1) or down (1), swapping with its
 * nearest positioned neighbour. Inline items have no position, so they are
 * skipped and never moved. `isShown` lets the caller also skip rows the user
 * cannot see (role or feature gated), so an arrow never swaps with an
 * invisible neighbour.
 */
export function moveItem(
  layout: SidebarLayout,
  id: SidebarItemId,
  direction: -1 | 1,
  isShown: (id: SidebarItemId) => boolean = () => true,
): SidebarLayout {
  if (isInline(id)) return layout;
  const from = layout.order.indexOf(id);
  if (from === -1) return layout;
  const skip = (candidate: SidebarItemId): boolean =>
    isInline(candidate) || !isShown(candidate);
  let to = from + direction;
  while (to >= 0 && to < layout.order.length && skip(layout.order[to])) {
    to += direction;
  }
  if (to < 0 || to >= layout.order.length) return layout;
  const order = [...layout.order];
  [order[from], order[to]] = [order[to], order[from]];
  return { ...layout, order };
}

export function setDensity(
  layout: SidebarLayout,
  density: SidebarDensity,
): SidebarLayout {
  return layout.density === density ? layout : { ...layout, density };
}

export function layoutsEqual(a: SidebarLayout, b: SidebarLayout): boolean {
  return (
    a.density === b.density &&
    a.order.length === b.order.length &&
    a.order.every((id, i) => id === b.order[i])
  );
}
